/**
 * Account surface contract: plan details, billing history, devices, and user settings.
 *
 * Import path: "./shared" (Plan 01-01 output)
 *
 * Domain invariants:
 *   - BillingStatusSchema: named enum prevents free strings like "PAID" or "Success"
 *   - foundingMemberNumber: nullable (non-founding members have null), min(1) when present
 *     (member numbers start at 1, never 0)
 */

import { z } from "zod";
import {
  SurfaceReadinessSchema,
  surfaceEnvelope,
} from "./shared";

// Suppress unused import warning — SurfaceReadinessSchema is used in surfaceEnvelope's return type.
void SurfaceReadinessSchema;

// --- Billing schemas ---

// Named enum for billing status. Prevents free-string variance ("PAID", "Success", "paid").
export const BillingStatusSchema = z.enum(["paid", "pending", "failed"]);

export type BillingStatus = z.infer<typeof BillingStatusSchema>;

// A single billing history row.
// date: human-readable date string (e.g., "Jan 15, 2026")
// amount: formatted currency string (e.g., "$192.00")
// status: constrained enum, not free string
export const BillingRowSchema = z.object({
  date: z.string().min(1),
  amount: z.string().min(1),
  status: BillingStatusSchema,
}).passthrough();

export type BillingRow = z.infer<typeof BillingRowSchema>;

// --- Device schema ---

// A connected session device.
// current: true only for the device making the current request.
export const DeviceSchema = z.object({
  name: z.string().min(1),
  current: z.boolean(),
}).passthrough();

export type Device = z.infer<typeof DeviceSchema>;

// --- Settings schema ---

// A single user preference setting.
// desc: description string, may be empty for simple toggles.
export const SettingSchema = z.object({
  key: z.string().min(1),
  label: z.string().min(1),
  desc: z.string(),
  enabled: z.boolean(),
}).passthrough();

export type Setting = z.infer<typeof SettingSchema>;

// --- Account plan schema ---

// Current subscription plan details.
// foundingMemberNumber: nullable — non-founding members receive null.
// When present, min(1) because member numbers start at 1 (never 0).
export const AccountPlanSchema = z.object({
  planTier: z.string().min(1),
  price: z.string().min(1),
  billingCycle: z.string().min(1),
  foundingMemberNumber: z.number().int().min(1).nullable(),
}).passthrough();

export type AccountPlan = z.infer<typeof AccountPlanSchema>;

// --- Surface data schema ---

export const AccountSurfaceDataSchema = z.object({
  billing: z.array(BillingRowSchema),
  devices: z.array(DeviceSchema),
  settings: z.array(SettingSchema),
  plan: AccountPlanSchema,
}).passthrough();

export type AccountSurfaceData = z.infer<typeof AccountSurfaceDataSchema>;

// --- Response schema ---

export const AccountSurfaceResponseSchema = surfaceEnvelope(AccountSurfaceDataSchema);

export type AccountSurfaceResponse = z.infer<typeof AccountSurfaceResponseSchema>;

// --- Fixture ---

// D-05: Representative account snapshot. Plan data from inline JSX in account.jsx.
// billing status normalized to canonical enum ("Paid" -> "paid").
export const ACCOUNT_FIXTURE: AccountSurfaceResponse = {
  status: "ready",
  data: {
    billing: [
      { date: "Jan 15, 2026", amount: "$192.00", status: "paid" },
      { date: "Jan 15, 2025", amount: "$192.00", status: "paid" },
    ],
    devices: [
      { name: "MacBook Pro",   current: true  },
      { name: "iPhone 15 Pro", current: false },
      { name: "iPad Air",      current: false },
    ],
    settings: [
      { key: "notifications", label: "Push notifications",  desc: "Get notified when new patterns are detected",             enabled: true  },
      { key: "autoSync",      label: "Auto-sync sources",   desc: "Automatically pull new data from connected sources",      enabled: true  },
      { key: "analytics",     label: "Usage analytics",     desc: "Help improve Terum by sharing anonymous usage data",      enabled: false },
      { key: "darkMode",      label: "Dark mode",           desc: "Use dark theme across the dashboard",                     enabled: true  },
    ],
    plan: {
      planTier: "Pro",
      price: "$20/mo",
      billingCycle: "Yearly",
      foundingMemberNumber: 847,
    },
  },
  readiness: {
    overall: 1.0,
    sections: {
      billing:  { ready: true, signalStrength: 1.0 },
      devices:  { ready: true, signalStrength: 1.0 },
      settings: { ready: true, signalStrength: 1.0 },
      plan:     { ready: true, signalStrength: 1.0 },
    },
  },
};
